import { CurrencyInput, TextArea, TextField } from '@/shared/components/ui';
import { formatAgentVariableLabel } from '@/shared/utils/agentVariables';
import { parseCurrencyNumber } from '@/shared/utils/number';

import type { AgentParametersFieldsProps } from '@/shared/types/agentParameters';

const parseBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'sim', 'yes', 'y', 's'].includes(normalized)) return true;
    if (['false', '0', 'não', 'nao', 'no', 'n'].includes(normalized)) return false;
  }
  return null;
};

export const AgentParametersFields = ({
  variables,
  values,
  errors = {},
  onChange,
  onClearError,
}: AgentParametersFieldsProps) => (
  <div className="flex flex-col gap-4">
    {variables.map((variable) => {
      const value = values[variable.name];
      const label = variable.label || formatAgentVariableLabel(variable.name, variable.description);
      const placeholder = variable.placeholderExample || undefined;
      const isRequired = variable.required || false;
      const errorKey = `agent_${variable.name}`;
      const errorMessage = errors[errorKey];
      const clearError = () => onClearError?.(errorKey);
      const normalizedType = typeof variable.type === 'string' ? variable.type.toLowerCase() : '';

      if (normalizedType === 'boolean') {
        const booleanValue = parseBoolean(value);
        const selectValue = booleanValue === null ? '' : booleanValue ? 'true' : 'false';
        return (
          <div key={variable.id ?? variable.name} className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <label
                className="text-caption-bold font-caption-bold text-default-font"
                htmlFor={variable.name}
              >
                {label}
              </label>
              {isRequired && <span className="text-error-600 text-[10px]">*</span>}
            </div>
            {variable.description && (
              <span className="text-caption text-subtext-color">{variable.description}</span>
            )}
            <select
              id={variable.name}
              value={selectValue}
              aria-invalid={Boolean(errorMessage)}
              aria-describedby={errorMessage ? `${errorKey}-erro` : undefined}
              onChange={(event) => {
                const selectedValue = event.target.value;
                onChange(variable.name, selectedValue === '' ? '' : selectedValue === 'true');
                clearError();
              }}
              className="focus:border-brand-500 focus:ring-brand-500/10 text-body text-default-font h-10 w-full rounded-[8px] border border-solid border-neutral-200 bg-white px-3 shadow-sm transition-all focus:ring-4 focus:outline-none"
            >
              <option value="">Selecione...</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
            {errorMessage && (
              <span id={`${errorKey}-erro`} role="alert" className="text-caption text-error-600">
                {errorMessage}
              </span>
            )}
          </div>
        );
      }

      if (variable.type === 'financeiro') {
        const numericValue =
          typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? (parseCurrencyNumber(value) ?? undefined)
              : undefined;
        return (
          <CurrencyInput
            key={variable.id ?? variable.name}
            label={isRequired ? `${label} *` : label}
            variant="outline"
            helpText={errorMessage || variable.description}
            error={Boolean(errorMessage)}
            value={numericValue}
            placeholder={placeholder || '0,00'}
            onChange={(nextValue) => {
              onChange(variable.name, nextValue);
              clearError();
            }}
          />
        );
      }

      if (variable.type === 'json') {
        const textValue = typeof value === 'string' ? value : value ? JSON.stringify(value) : '';
        return (
          <TextArea
            key={variable.id ?? variable.name}
            label={isRequired ? `${label} *` : label}
            variant="outline"
            helpText={errorMessage || variable.description}
            error={Boolean(errorMessage)}
          >
            <TextArea.Input
              placeholder={placeholder || '{}'}
              value={textValue}
              onChange={(event) => {
                onChange(variable.name, event.target.value);
                clearError();
              }}
            />
          </TextArea>
        );
      }

      if (variable.type === 'number') {
        const textValue =
          typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
        return (
          <TextField
            key={variable.id ?? variable.name}
            label={isRequired ? `${label} *` : label}
            variant="outline"
            helpText={errorMessage || variable.description}
            error={Boolean(errorMessage)}
          >
            <TextField.Input
              type="number"
              inputMode="decimal"
              placeholder={placeholder}
              value={textValue}
              onChange={(event) => {
                const raw = event.target.value;
                const parsed = Number(raw);
                onChange(variable.name, raw ? (Number.isFinite(parsed) ? parsed : raw) : '');
                clearError();
              }}
            />
          </TextField>
        );
      }

      if (variable.type === 'date') {
        return (
          <TextField
            key={variable.id ?? variable.name}
            label={isRequired ? `${label} *` : label}
            variant="outline"
            helpText={errorMessage || variable.description}
            error={Boolean(errorMessage)}
          >
            <TextField.Input
              type="date"
              value={typeof value === 'string' ? value : ''}
              onChange={(event) => {
                onChange(variable.name, event.target.value);
                clearError();
              }}
            />
          </TextField>
        );
      }

      const textValue =
        typeof value === 'string'
          ? value
          : value === undefined || value === null
            ? ''
            : String(value);
      return (
        <TextField
          key={variable.id ?? variable.name}
          label={isRequired ? `${label} *` : label}
          variant="outline"
          helpText={errorMessage || variable.description}
          error={Boolean(errorMessage)}
        >
          <TextField.Input
            placeholder={placeholder}
            value={textValue}
            onChange={(event) => {
              onChange(variable.name, event.target.value);
              clearError();
            }}
          />
        </TextField>
      );
    })}
  </div>
);
